// LeftController - 左側の操作コントローラー
// Undo/Redo + タップ/削除モード + Zoom

import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import { RootState } from "app/store";
import { useScale, usePlaySound } from "common/utils";
import { actions } from "features/builder/slice";
import { useActionCommand } from "features/builder/hook";
import { useBuilderScene } from "features/builder/context";

export const LeftController = () => {
  const play = usePlaySound();
  const dispatch = useDispatch();
  const scene = useBuilderScene();
  const { scale, ratio } = useScale();
  const { undo, redo, canUndo, canRedo } = useActionCommand();
  const actionMenu = useSelector((state: RootState) => state.builder.actionMenu);

  const defaultRadius = useMemo(
    () => (ratio > 1 ? (scale > 0.7 ? 20 / scale : 40 * scale) : 20 / scale),
    [scale, ratio],
  );

  const handleUndo = () => {
    if (canUndo()) {
      play();
      undo();
    }
  };

  const handleRedo = () => {
    if (canRedo()) {
      play();
      redo();
    }
  };

  const handleSelectTap = () => {
    play();
    dispatch(actions.updateActionMenu({ key: "tap", value: true, exclusive: true }));
    dispatch(actions.selectSlot(null));
    dispatch(actions.selectedAssetMesh(null));
  };

  const handleSelectEraser = () => {
    play();
    dispatch(actions.updateActionMenu({ key: "eraser", value: true, exclusive: true }));
    dispatch(actions.selectSlot(null));
    dispatch(actions.selectedAssetMesh(null));
  };

  const handleZoom = (zoom: number) => () => {
    if (!scene) return;
    const camera = scene.getCameraByName("camera") as ArcRotateCamera;
    if (camera) {
      play();
      if (zoom > 0) {
        camera.radius -= 1;
      } else if (zoom < 0) {
        camera.radius += 1;
      } else {
        camera.radius = defaultRadius;
        camera.alpha = Math.PI / 4;
        camera.beta = Math.PI / 4;
      }
    }
  };

  return (
    <div className="flex-col-el absolute left-2 top-1/2 -translate-y-1/2 z-[10000] pointer-events-auto">
      <div className="flex-col-el gap-2 p-2 rounded-xl bg-white/80 backdrop-blur-sm border-2 border-white shadow-lg">
        {/* Undo/Redo */}
        <div className="flex-col-el gap-1">
          <button
            onClick={handleUndo}
            disabled={!canUndo()}
            className={`flex-col-el flex-center w-16 h-16 rounded-lg transition-all duration-150
              ${
                canUndo()
                  ? "bg-gray-100 hover:bg-gray-200 active:scale-95 cursor-pointer"
                  : "bg-gray-50 opacity-40 cursor-not-allowed"
              }`}
          >
            <UndoIcon className="text-gray-600" style={{ fontSize: 32 }} />
          </button>
          <button
            onClick={handleRedo}
            disabled={!canRedo()}
            className={`flex-col-el flex-center w-16 h-16 rounded-lg transition-all duration-150
              ${
                canRedo()
                  ? "bg-gray-100 hover:bg-gray-200 active:scale-95 cursor-pointer"
                  : "bg-gray-50 opacity-40 cursor-not-allowed"
              }`}
          >
            <RedoIcon className="text-gray-600" style={{ fontSize: 32 }} />
          </button>
        </div>

        {/* 区切り線 */}
        <div className="w-full h-[2px] bg-gray-200 rounded-full" />

        {/* タップ/削除モード */}
        <div className="flex-col-el gap-1">
          <button
            onClick={handleSelectTap}
            className={`flex-col-el flex-center w-16 h-16 rounded-lg transition-all duration-150 active:scale-95 cursor-pointer
              ${
                actionMenu.tap
                  ? "bg-blue-100 border-2 border-blue-400"
                  : "bg-gray-100 hover:bg-gray-200 border-2 border-transparent"
              }`}
          >
            <TouchAppOutlinedIcon
              className={actionMenu.tap ? "text-blue-600" : "text-gray-600"}
              style={{ fontSize: 32 }}
            />
          </button>
          <button
            onClick={handleSelectEraser}
            className={`flex-col-el flex-center w-16 h-16 rounded-lg transition-all duration-150 active:scale-95 cursor-pointer
              ${
                actionMenu.eraser
                  ? "bg-red-100 border-2 border-red-400"
                  : "bg-gray-100 hover:bg-gray-200 border-2 border-transparent"
              }`}
          >
            <DeleteOutlineIcon
              className={actionMenu.eraser ? "text-red-600" : "text-gray-600"}
              style={{ fontSize: 32 }}
            />
          </button>
        </div>

        {/* 区切り線 */}
        <div className="w-full h-[2px] bg-gray-200 rounded-full" />

        {/* Zoom */}
        <div className="flex-col-el gap-1">
          <button
            onClick={handleZoom(1)}
            className="flex-col-el flex-center w-16 h-16 rounded-lg transition-all duration-150 active:scale-95 cursor-pointer bg-blue-100 hover:bg-blue-200"
          >
            <ZoomInIcon className="text-blue-600" style={{ fontSize: 32 }} />
          </button>
          <button
            onClick={handleZoom(0)}
            className="flex-col-el flex-center w-16 h-16 rounded-lg transition-all duration-150 active:scale-95 cursor-pointer bg-blue-100 hover:bg-blue-200"
          >
            <CenterFocusStrongIcon className="text-blue-600" style={{ fontSize: 32 }} />
          </button>
          <button
            onClick={handleZoom(-1)}
            className="flex-col-el flex-center w-16 h-16 rounded-lg transition-all duration-150 active:scale-95 cursor-pointer bg-blue-100 hover:bg-blue-200"
          >
            <ZoomOutIcon className="text-blue-600" style={{ fontSize: 32 }} />
          </button>
        </div>
      </div>
    </div>
  );
};
